import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { FieldItem } from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import FieldNames from '@lib/enums/fieldNames/teamFieldNames';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import UserChipsListField from '@lib/components/UserChipListField/UserChipListField';
import Typography from '@lib/components/Typography/Typography';
import UserOption from '@lib/components/Select/components/UserOption';
import {
  getFieldValidation,
  getOptionsArrayValidation,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsChipColor from '@lib/components/RadioButtonAsChipColor/RadioButtonAsChipColor';
import formStyles from '@lib/assets/styles/Form.module.scss';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import { UseTeamQueriesResult } from './useTeamQueries';
import TeamAccessField from '../TeamAccess/TeamAccessField';

type Args = {
  teamId?: string;
  getTeamMembersLoadOptions: GetLoadOptions;
  getAssetCategoriesLoadOptions: GetLoadOptions;
  getDocumentationCategoriesLoadOptions: GetLoadOptions;
  getInterventionCategoriesLoadOptions: GetLoadOptions;
  getDepartmentsLoadOptions: GetLoadOptions;
  openDepartmentsModalTableMultiselect: () => void;
  t: TFunction<'translation', undefined>;
  useTeamQueriesResult: UseTeamQueriesResult;
  membersIds: { id: string }[];
};

export default function getTeamFormFields(args: Args): FieldItem[] {
  const {
    getTeamMembersLoadOptions,
    getAssetCategoriesLoadOptions,
    getDocumentationCategoriesLoadOptions,
    getInterventionCategoriesLoadOptions,
    getDepartmentsLoadOptions,
    openDepartmentsModalTableMultiselect,
    teamId,
    useTeamQueriesResult,
    t,
    membersIds,
  } = args;
  return [
    {
      name: FieldNames.DetailsTitle,
      customRender: () => (
        <Typography variant="h3" key="details">
          {t('team-form-details-title')}
        </Typography>
      ),
    },
    {
      name: FieldNames.Name,
      label: t('team-name'),
      element: Input,
      validation: getFieldValidation({
        required: true,
        t,
      }),
    },
    {
      name: FieldNames.Description,
      label: t('description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
    },
    {
      name: FieldNames.Color,
      label: t('label-color'),
      element: RadioButtonGroup,
      validation: getOptionValidation({ t, required: true }),
      componentProps: {
        options: getColorOptions({ t }),
        RadioButtonComponent: RadioButtonAsChipColor,
        wrapView: RadioButtonWrapViews.colors,
      },
    },
    {
      name: FieldNames.MembersAssignationTitle,
      customRender: () => (
        <Typography
          variant="h3"
          key="members-assignation-title"
          className={formStyles.blockStart}
        >
          {t('members-assignation-title')}
        </Typography>
      ),
    },
    {
      name: FieldNames.Users,
      label: t('find-team-members'),
      element: Select,
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getTeamMembersLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
        disabledTooltipProps: {
          body: t('choose-site-first'),
        },
      } as SelectProps,
      hideErrorsSpace: true,
    },
    {
      name: FieldNames.UsersList,
      element: UserChipsListField,
      componentProps: {
        fieldName: FieldNames.Users,
        teamId,
        ids: membersIds,
      },
      isUseWatch: true,
    },
    {
      name: FieldNames.MembersAssignationMessage,
      customRender: () => (
        <SupportiveMessage
          text={t('team-members-field-message')}
          color={SupportiveMessageColor.Yellow}
        />
      ),
    },
    {
      name: FieldNames.DepartmentLinks,
      label: t('team-department-links-label'),
      element: Select,
      isOptionalFn: () => true,
      description: t('team-department-links-description'),
      validation: getOptionsArrayValidation({ t }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        getLoadOptions: getDepartmentsLoadOptions,
        defaultOptions: false,
        isClearable: true,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        onSelectWrapperMouseDown: openDepartmentsModalTableMultiselect,
        components: {
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      } as SelectProps,
      hideErrorsSpace: true,
    },
    {
      name: FieldNames.TeamAccessRights,
      customRender: () => (
        <Typography
          variant="h3"
          key="team-access-rights"
          className={formStyles.blockStart}
        >
          {t('team-access-rights')}
        </Typography>
      ),
    },
    {
      name: FieldNames.Accesses,
      element: TeamAccessField,
      componentProps: {
        getAssetCategoriesLoadOptions,
        getDocumentationCategoriesLoadOptions,
        getInterventionCategoriesLoadOptions,
        useTeamQueryHookResult: useTeamQueriesResult.useTeamQueryHookResult,
      },
      isUseWatch: true,
      validation: Yup.object(),
    },
    {
      name: FieldNames.TeamAccessesMessage,
      customRender: () => (
        <SupportiveMessage
          text={t('team-accesses-field-message')}
          color={SupportiveMessageColor.Yellow}
        />
      ),
    },
  ];
}
